import React, { useState, useEffect } from "react";
import ReusableTableDiff from "./ReusableTableDiff";
import AddSkillModal from "./AddSkillModal";
import EditSkillModal from "./EditSkillModal";
import { Box, useTheme, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { tokens } from "../../../theme";
import { recruitSkillsApis } from "../../../Data/axios";
import { useTableActions } from "./hooks/useTableActions";

const Skills = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [skillsData, setSkillsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queries, setQueries] = useState({
    page: 1,
    per_page: 10,
  });
  const [open, setOpen] = useState(false);

  // Consolidated fetch function to avoid duplication
  const fetchSkills = async (params = {}) => {
    try {
      setLoading(true);
      const response = await recruitSkillsApis.getSkills({
        page: params.page || queries.page,
        per_page: params.per_page || queries.per_page,
      });
      setSkillsData(response.data.skills.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching skills:", error);
      setSkillsData([]);
      setLoading(false);
    }
  };

  // Custom hook for table actions
  const tableActions = useTableActions(recruitSkillsApis, fetchSkills);
  const { showEdit, editData, setShowEdit, setEditData } = tableActions;

  // Initial data fetch
  useEffect(() => {
    fetchSkills();
  }, [queries.page, queries.per_page]);

  // Table headers configuration
  const headers = [{ key: "name", label: "Name", sortable: true }];

  const handlePageChange = (newPage) => {
    setQueries({ ...queries, page: newPage });
  };

  const handlePerPageChange = (newPerPage) => {
    setQueries({ ...queries, per_page: newPerPage, page: 1 });
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleEditClose = () => {
    setShowEdit(false);
    setEditData(null);
  };

  const handleSaveSkill = () => {
    fetchSkills();
    handleClose();
  };

  const handleUpdateSkill = () => {
    fetchSkills();
    handleEditClose();
  };

  return (
    <>
      <div className="w-full bg-white shadow-sm mb-6 border-b border-gray-200 font-ubuntu">
        <div className="w-full py-3 px-4">
          <div className="flex items-center gap-x-3">
            {/* Search input */}
            <div className="relative">
              <div className="flex items-center border border-gray-300 rounded bg-gray-200">
                <div className="pl-2 pr-2">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Start typing to search"
                  className="py-1 px-2 text-sm w-72 border-none rounded-none text-black focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Box display="flex" justifyContent="flex-start">
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpen}
          sx={{
            bgcolor: "#4285F4",
            color: "white",
            "&:hover": {
              bgcolor: "#3367D6",
            },
            marginTop: "32px",
            marginLeft: "18px",
            paddingY: "8px",
            borderRadius: "4px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Add Skill
        </Button>
      </Box>

      <ReusableTableDiff
        headers={headers}
        data={skillsData}
        rowKey="id"
        defaultSortKey=""
        tableActions={tableActions}
        tableApis={recruitSkillsApis}
        onTableUpdate={fetchSkills}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        queries={queries}
        type="skills"
        tableClassName="skills-table"
      />

      {/* Add Skill Modal Component */}
      <AddSkillModal
        open={open}
        handleClose={handleClose}
        handleSave={handleSaveSkill}
        tableApis={recruitSkillsApis}
      />

      {/* Edit Skill Modal Component */}
      <EditSkillModal
        open={showEdit}
        handleClose={handleEditClose}
        handleSave={handleUpdateSkill}
        tableApis={recruitSkillsApis}
        skillToEdit={editData}
      />
    </>
  );
};

export default Skills;
